/**
 * The list, kept beside an open policy.
 *
 * It is the page of the library the policy was opened from: the same view,
 * filters, order and search, asked of the server the same way, and the same
 * page of the answer (useLibraryRoute, which the library's own table uses).
 * It cannot filter differently from the table or fall out of step with it,
 * because it is not a second list. It holds one page, like the table, and
 * moves to the next or the previous page with its own two buttons.
 */
import { Link } from 'react-router-dom';
import { PolicyLayoutChoice, type PolicyLayout } from './policyLayout';
import { isNamedSet } from './library/libraryQuery';
import { rangeText } from './library/LibraryPager';
import { rowStatus, VIEW_LABEL } from './library/libraryText';
import { useLibraryRoute } from './library/useLibrary';
import styles from './PolicyListPane.module.css';

export interface PolicyListPaneProps {
  selectedId: string | null;
  layout: PolicyLayout;
  onLayoutChange: (next: PolicyLayout) => void;
}

export function PolicyListPane({ selectedId, layout, onLayoutChange }: PolicyListPaneProps) {
  const { state, data, now, search, linkState, next, previous } = useLibraryRoute();
  const query = search ? `?${search}` : '';
  const name = isNamedSet(state) ? 'Just uploaded' : data.mode === 'search' ? `Search: ${state.text.trim()}` : VIEW_LABEL[state.view];

  return (
    <div className={styles.pane}>
      <div className={styles.head}>
        <p className={styles.scope}>{name}</p>
        <p className={styles.count} role="status">
          {data.isLoading ? 'Loading…' : data.error ? 'Could not load the list' : rangeText(data) || 'No policies'}
        </p>
      </div>

      <div className={styles.controls}>
        {/* Here as well as on the library, because the way out of the frame should be inside the frame. */}
        <PolicyLayoutChoice value={layout} onChange={onLayoutChange} idPrefix="rail-layout" />
        <Link to={`/policies${query}`} state={linkState} className={styles.change}>Back to Policies</Link>
      </div>

      {data.error ? (
        <p className={styles.empty}>
          The list could not be loaded. <button type="button" className={styles.pageLink} onClick={data.retry}>Try again</button>
        </p>
      ) : !data.isLoading && data.rows.length === 0 && (
        <p className={styles.empty}>Nothing in this list now.</p>
      )}

      <ul className={styles.list} aria-busy={data.isUpdating || undefined}>
        {data.rows.map((row) => {
          const status = rowStatus(row, now);
          const selected = row.id === selectedId;
          return (
            <li key={row.id}>
              <Link
                to={`/policy/${row.id}${query}`}
                state={linkState}
                className={selected ? `${styles.row} ${styles.selected}` : styles.row}
                // The pane is a list of links, so the open one is the current page rather than a "selected" widget value.
                aria-current={selected ? 'page' : undefined}
              >
                <span className={styles.number}>{row.policyNumber || '—'}</span>
                <span className={styles.title}>{row.title}</span>
                {/* Words, not a colored dot: a dot with its meaning in a tooltip tells a reader nothing. */}
                {status.tone !== 'ok' && <span className={styles.state}>{status.label}</span>}
              </Link>
            </li>
          );
        })}
      </ul>

      {(data.hasPrevious || data.hasNext) && (
        <div className={styles.pages}>
          <button type="button" className={styles.pageLink} disabled={!data.hasPrevious || data.isUpdating} onClick={previous}>Previous page</button>
          <button type="button" className={styles.pageLink} disabled={!data.hasNext || data.isUpdating} onClick={next}>Next page</button>
        </div>
      )}
    </div>
  );
}
